"use client";

// ============================================================
// CLUBHUB – Nachrichten: die Empfaenger im Versandprotokoll
// ============================================================
// Jede Person mit Weg, Status und — wenn es schiefging — dem Grund
// in Worten statt eines SMTP-Codes allein. „Adresse prüfen“ fuehrt
// zum Mitglied, „Erneut senden“ schickt die Nachricht noch einmal an
// alle Fehlgeschlagenen (nach dem Pruefen der Adressen).
//
// Gefiltert wird hier im Browser: das Protokoll einer Nachricht ist
// klein genug, und der Filter soll beim Durchklicken nicht laden.
// ============================================================
import { useMemo, useState } from "react";
import Link from "next/link";
import { ChevronDown, RotateCcw, Search, UserRoundPen } from "lucide-react";
import type { MailingChannel } from "@/generated/prisma/enums";
import { Button } from "@/components/ui/button";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { Schiene, Zaehlknopf } from "@/components/liste";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn, formatDateTime } from "@/lib/utils";
import type { MailingRecipientView } from "@/lib/nachrichten/views";
import { WegEtikett, ZustellBadge } from "./kanal";

type Filter = "" | "zugestellt" | "geoeffnet" | "ungeoeffnet" | "fehler" | "ausstehend" | "brief";

const FILTER: { schluessel: Filter; label: string; test: (e: MailingRecipientView) => boolean; nurMail?: boolean; ton?: "gefahr" }[] = [
  { schluessel: "", label: "Alle", test: () => true },
  { schluessel: "zugestellt", label: "Zugestellt", test: (e) => e.status === "DELIVERED" || e.status === "OPENED" || e.status === "SENT" },
  { schluessel: "geoeffnet", label: "Geöffnet", test: (e) => e.status === "OPENED", nurMail: true },
  { schluessel: "ungeoeffnet", label: "Nicht geöffnet", test: (e) => e.weg === "EMAIL" && e.status === "DELIVERED", nurMail: true },
  { schluessel: "brief", label: "Per Brief", test: (e) => e.weg === "LETTER" },
  { schluessel: "ausstehend", label: "Ausstehend", test: (e) => e.status === "PENDING" },
  { schluessel: "fehler", label: "Fehler", test: (e) => e.status === "BOUNCED" || e.status === "FAILED", ton: "gefahr" },
];

const istFehler = (e: MailingRecipientView) => e.status === "BOUNCED" || e.status === "FAILED";

function Zeitpunkt({ e }: { e: MailingRecipientView }) {
  if (e.openedAt) return <>geöffnet {formatDateTime(e.openedAt)}</>;
  if (e.sentAt) return <>{e.weg === "LETTER" ? "übergeben" : "gesendet"} {formatDateTime(e.sentAt)}</>;
  return <span className="text-text-tertiary">–</span>;
}

/** Liegt es an der Adresse? Ein abgebrochener Versand oder ein Spamfilter ist kein Fall fuer die Stammdaten. */
const anDerAdresse = (e: MailingRecipientView) => e.status === "BOUNCED" || /Nummer|Postfach|Domain|Rücklauf/.test(e.error ?? "");

function Pruefen({ e }: { e: MailingRecipientView }) {
  if (!anDerAdresse(e) || !e.contactId) return null;
  return (
    <Link
      href={`/mitglieder/${e.contactId}`}
      className="text-caption text-text-secondary hover:bg-bg-tertiary hover:text-text-primary inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 whitespace-nowrap transition-colors"
    >
      <UserRoundPen className="h-3.5 w-3.5" aria-hidden />
      Adresse prüfen
    </Link>
  );
}

export function ProtokollListe({
  empfaenger: start,
  channel,
  ausgeschlossen,
  erneutMoeglich,
}: {
  empfaenger: MailingRecipientView[];
  channel: MailingChannel;
  /** Wer bei der Auswahl war, aber nicht angeschrieben wurde — mit Grund. */
  ausgeschlossen: { contactId: string; name: string; grund: string }[];
  erneutMoeglich: boolean;
}) {
  const [liste, setListe] = useState(start);
  const [filter, setFilter] = useState<Filter>(start.some(istFehler) ? "fehler" : "");
  const [q, setQ] = useState("");
  const [rausOffen, setRausOffen] = useState(false);

  const fehlgeschlagen = liste.filter(istFehler);
  const zaehler = useMemo(() => Object.fromEntries(FILTER.map((f) => [f.schluessel, liste.filter(f.test).length])), [liste]);
  const nadel = q.trim().toLowerCase();
  const gezeigt = liste.filter((e) => FILTER.find((f) => f.schluessel === filter)!.test(e)).filter((e) => !nadel || `${e.name} ${e.address}`.toLowerCase().includes(nadel));
  const sichtbareFilter = FILTER.filter((f) => (!f.nurMail || channel === "EMAIL") && (f.schluessel === "" || f.schluessel === filter || zaehler[f.schluessel]! > 0));

  async function erneut() {
    const n = fehlgeschlagen.length;
    const ok = await confirmDialog({
      title: `Erneut an ${n} ${n === 1 ? "Empfänger" : "Empfänger"} senden?`,
      description: "Nur an die mit Fehler oder unzustellbarer Adresse. Prüfe vorher die Adressen — an dieselbe falsche Adresse kommt die Nachricht wieder nicht an.",
      confirmLabel: "Erneut senden",
    });
    if (!ok) return;
    // BACKEND: resendFailed(mailingId) — neue Zustellversuche fuer alle BOUNCED/FAILED, mit der heute hinterlegten Adresse
    setListe((l) => l.map((e) => (istFehler(e) ? { ...e, status: "PENDING", error: null, sentAt: null, openedAt: null } : e)));
    setFilter("ausstehend");
    toast.success(`${n} ${n === 1 ? "Nachricht" : "Nachrichten"} erneut eingereiht`, { description: "Der Status aktualisiert sich, sobald der Versanddienst antwortet." });
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Schiene className="max-w-full overflow-x-auto [scrollbar-width:none]">
          {sichtbareFilter.map((f) => (
            <Zaehlknopf
              key={f.schluessel || "alle"}
              label={f.label}
              anzahl={zaehler[f.schluessel] ?? 0}
              aktiv={filter === f.schluessel}
              ton={f.ton ?? "normal"}
              onClick={() => setFilter(filter === f.schluessel ? "" : f.schluessel)}
            />
          ))}
        </Schiene>
        <label className="bg-bg-primary border-border focus-within:border-accent/40 focus-within:ring-accent/30 flex h-9 min-w-0 flex-1 basis-44 items-center gap-2 rounded-lg border px-2.5 focus-within:ring-2">
          <Search className="text-text-tertiary h-4 w-4 shrink-0" aria-hidden />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name oder Adresse …"
            aria-label="Empfänger durchsuchen"
            className="text-body-sm placeholder:text-text-tertiary min-w-0 flex-1 bg-transparent outline-none"
          />
        </label>
        {erneutMoeglich && fehlgeschlagen.length > 0 && (
          <Button variant="outline" onClick={erneut}>
            <RotateCcw className="h-4 w-4" aria-hidden />
            Erneut an Fehlgeschlagene ({fehlgeschlagen.length})
          </Button>
        )}
      </div>

      {gezeigt.length === 0 ? (
        <p className="text-body-sm text-text-tertiary border-border rounded-lg border border-dashed px-4 py-8 text-center">
          {liste.length === 0 ? "Noch hat niemand diese Nachricht bekommen." : "Niemand passt zu diesem Filter."}
        </p>
      ) : (
        <>
          <Table viewportClassName="max-md:hidden">
            <TableHeader className="bg-bg-elevated">
              <TableRow>
                <TableHead className="pl-0">NAME</TableHead>
                <TableHead>ADRESSE</TableHead>
                <TableHead>WEG</TableHead>
                <TableHead>STATUS</TableHead>
                <TableHead>ZEITPUNKT</TableHead>
                <TableHead className="pr-0 text-right">
                  <span className="sr-only">Aktion</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {gezeigt.map((e) => (
                <TableRow key={e.id} className={cn("border-border-subtle border-b", istFehler(e) && "bg-danger-subtle/40 hover:bg-danger-subtle/60")}>
                  <TableCell className="py-2.5 pl-0">
                    {e.contactId ? (
                      <Link href={`/mitglieder/${e.contactId}`} className="text-text-primary hover:text-accent font-medium">
                        {e.name}
                      </Link>
                    ) : (
                      e.name
                    )}
                    {e.error && <p className="text-caption text-danger mt-0.5">{e.error}</p>}
                  </TableCell>
                  <TableCell className="text-text-secondary max-w-64 truncate">{e.address}</TableCell>
                  <TableCell>
                    <WegEtikett weg={e.weg} />
                  </TableCell>
                  <TableCell>
                    <ZustellBadge status={e.status} weg={e.weg} />
                  </TableCell>
                  <TableCell className="text-caption text-text-secondary whitespace-nowrap tabular-nums">
                    <Zeitpunkt e={e} />
                  </TableCell>
                  <TableCell className="pr-0 text-right">
                    <Pruefen e={e} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <ul className="space-y-2 md:hidden">
            {gezeigt.map((e) => (
              <li key={e.id} className={cn("rounded-lg px-3 py-2.5 ring-1 ring-inset", istFehler(e) ? "bg-danger-subtle/40 ring-danger/20" : "bg-bg-secondary ring-border-subtle")}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {e.contactId ? (
                      <Link href={`/mitglieder/${e.contactId}`} className="text-body-sm text-text-primary block truncate font-medium">
                        {e.name}
                      </Link>
                    ) : (
                      <p className="text-body-sm text-text-primary truncate font-medium">{e.name}</p>
                    )}
                    <p className="text-caption text-text-tertiary truncate">{e.address}</p>
                  </div>
                  <ZustellBadge status={e.status} weg={e.weg} />
                </div>
                {e.error && <p className="text-caption text-danger mt-1.5">{e.error}</p>}
                <div className="text-caption text-text-secondary mt-1.5 flex items-center justify-between gap-3 tabular-nums">
                  <span className="flex items-center gap-3">
                    <WegEtikett weg={e.weg} />
                    <Zeitpunkt e={e} />
                  </span>
                  <Pruefen e={e} />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {ausgeschlossen.length > 0 && (
        <div className="border-border-subtle border-t pt-3">
          <button type="button" onClick={() => setRausOffen((o) => !o)} aria-expanded={rausOffen} className="text-body-sm text-text-secondary hover:text-text-primary flex items-center gap-1.5">
            <ChevronDown className={cn("h-4 w-4 transition-transform", rausOffen && "rotate-180")} aria-hidden />
            Nicht angeschrieben <span className="tabular-nums">({ausgeschlossen.length})</span>
          </button>
          {rausOffen && (
            <ul className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
              {ausgeschlossen.map((a) => (
                <li key={a.contactId} className="text-body-sm flex items-baseline justify-between gap-3 py-0.5">
                  <Link href={`/mitglieder/${a.contactId}`} className="text-text-primary hover:text-accent truncate">
                    {a.name}
                  </Link>
                  <span className="text-caption text-text-tertiary shrink-0">{a.grund}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
